/** @jsxImportSource @emotion/react */
import Typography from '@mui/material/Typography'
import { useCallback, useState } from 'react'
import { BaseModal } from 'components/Modals/BaseModal'
import { CreateQuestionBankCategoryInput } from '__generatedTypes__/globalTypes'
import { SubjectExtended } from 'hooks/usePlatformsAndSubjects'
import { toast } from 'utils/toast'
import { useCategoriesApi, QuestionBankCategory } from '../useCategoriesApi'
import { Form } from './Form'
import * as classes from './styles'

export type EditCategoryModalProps = {
  open: boolean
  subject: SubjectExtended
  questionBankCategory: QuestionBankCategory
  onClose: () => void
  onRefetch: () => void
}

export const EditCategoryModal = ({
  open,
  subject,
  questionBankCategory,
  onClose,
  onRefetch,
  ...props
}: EditCategoryModalProps) => {
  const [submitting, setSubmitting] = useState(false)
  const categoriesApi = useCategoriesApi()

  const handleSubmit = useCallback(
    async (category: CreateQuestionBankCategoryInput) => {
      try {
        await categoriesApi.updateCategory(subject, questionBankCategory.id, {
          tabIndex: subject.tabIndex,
          ...category,
        })
        onClose()
        onRefetch()

        toast.success('Category updated successfully')
      } catch (e) {
        toast.error('Failed to update category')
      }
    },
    [subject, questionBankCategory, onClose, onRefetch]
  )

  const handleRemove = useCallback(async () => {
    setSubmitting(true)
    try {
      await categoriesApi.removeCategory(subject, questionBankCategory.id)
      onClose()
      onRefetch()
      toast.success('Category removed successfully')
    } catch (e) {
      toast.error('Failed to remove category')
    }
    setSubmitting(false)
  }, [subject, questionBankCategory, onClose, onRefetch])

  return (
    <BaseModal open={open} onClose={onClose} {...props}>
      <Typography css={classes.title} variant="title2">
        Edit Question Bank Category
      </Typography>
      <Form
        categoryOrSubjectId={questionBankCategory}
        onSubmit={handleSubmit}
        onRemove={handleRemove}
        submitting={submitting}
      />
    </BaseModal>
  )
}
